import type { ArticleResponse } from '@shared/api-utils';
import { getConfig, ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { JsonLd } from '../../JsonLd/JsonLd';

type MagazineCategoryStructuredDataProps = {
  categoryName: string;
  articles: readonly ArticleResponse[];
  /** Current page path (may include `/page/N` for paginated category pages). */
  pagePath: string;
  /** Canonical category hub path (always page 1, without `/page/N`). */
  categoryPath: string;
};

function buildAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl.replace(/\/$/, '')}${normalisedPath}`;
}

function MagazineCategoryStructuredData({ categoryName, articles, pagePath, categoryPath }: MagazineCategoryStructuredDataProps) {
  const { baseUrl } = getConfig();
  const t = useTranslations('magazine.breadcrumbs');
  const pageUrl = buildAbsoluteUrl(baseUrl, pagePath);
  const categoryUrl = buildAbsoluteUrl(baseUrl, categoryPath);

  // TODO(WEBD-621): articles are NOT filtered by category — ItemList reflects all articles on
  // this page, not just those in the given category. Update once the API supports category filter.
  const collectionPageData = {
    '@type': 'CollectionPage' as const,
    name: categoryName,
    url: pageUrl,
    mainEntity: {
      '@type': 'ItemList' as const,
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem' as const,
        position: index + 1,
        name: article.title,
        url: buildAbsoluteUrl(baseUrl, article.seoInfo.canonicalUrl),
      })),
    },
  };

  const breadcrumbListData = {
    '@type': 'BreadcrumbList' as const,
    itemListElement: [
      {
        '@type': 'ListItem' as const,
        name: t('site_name'),
        item: baseUrl,
      },
      {
        '@type': 'ListItem' as const,
        name: t('magazine_home_name'),
        item: buildAbsoluteUrl(baseUrl, ROUTES.MAGAZINE.HOME),
      },
      {
        '@type': 'ListItem' as const,
        name: categoryName,
        item: categoryUrl,
      },
    ].map((item, index) => ({ ...item, position: index + 1 })),
  };

  return (
    <>
      <JsonLd
        json={{
          '@context': 'https://schema.org',
          ...collectionPageData,
        }}
        data-testid="magazine-category-structured-data"
      />
      <JsonLd
        json={{
          '@context': 'https://schema.org',
          ...breadcrumbListData,
        }}
        data-testid="magazine-category-breadcrumb-structured-data"
      />
    </>
  );
}

export { MagazineCategoryStructuredData, type MagazineCategoryStructuredDataProps };
